import { useQueryClient } from '@tanstack/react-query';
import { Divider } from 'primereact/divider';
import { Skeleton } from 'primereact/skeleton';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import GlobeIcon from '../../../../../../assets/icons/Globe';
import { useGetComponentDetails } from '../../../../../../hooks/api/components';
import { useAuth } from '../../../../../../hooks/auth/useAuth';
import ActivePcfDetails from '../../../../../../shared/components/active-pcf-details/ActivePcfDetails';
import { mapComponentToActivePcf } from '../../../../../../shared/components/active-pcf-details/data-utils';
import {
  CountryLabelToCodeMap,
  RegionLabelToCodeMap,
} from '../../../../../../shared/helpers/country-region-helpers';
import ComponentInplaceEdit from '../../../component-inplace-edit/ComponentInplaceEdit';
import styles from './ComponentDetailsTab.module.css';

const ComponentDetailsTab = (): JSX.Element => {
  const { componentId } = useParams() as { componentId: string };
  const { user } = useAuth();

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Fetch component details
  // TODO: A Context can be used in the future to store component details and footprint details as well. We currently do it in ProductDetailsPage
  const { data: componentDetails, isLoading } = useGetComponentDetails(componentId, {
    retry: 1,
    onSuccess: () => {
      // Invalidate components list since visiting component details page will update the status to completed
      queryClient.invalidateQueries({
        predicate: (query) => {
          return query.queryKey[0] === 'components' && typeof query.queryKey[1] === 'object';
        },
      });
    },
  });

  // Function to get location flag based on geographyCountry or geographyRegionOrSubregion fields
  const getFlagIcon = (): JSX.Element | null => {
    // If country is specified and known, return the flag icon
    if (
      componentDetails?.pcf?.geographyCountry &&
      Object.keys(CountryLabelToCodeMap).includes(componentDetails?.pcf?.geographyCountry)
    ) {
      return (
        <span
          className={classNames(
            `fi fi-${CountryLabelToCodeMap[
              componentDetails?.pcf?.geographyCountry
            ]?.toLowerCase()} border-round align-items-center justify-content-center`,
            styles['flag-icon'],
          )}
        />
      );
    }

    // If region is specified and known, return the globe icon
    // If country is not specified, show globe icon
    if (
      componentDetails?.pcf?.geographyRegionOrSubregion &&
      Object.keys(RegionLabelToCodeMap).includes(componentDetails?.pcf?.geographyRegionOrSubregion)
    ) {
      return (
        <div className={styles['globe-icon']}>
          <GlobeIcon />
        </div>
      );
    }

    // If neither is specified or known, return nothing

    return null;
  };

  // Function to get location text based on geographyCountry or geographyRegionOrSubregion fields
  const getLocationText = (): string => {
    // If country is specified and known, return the country name
    if (
      componentDetails?.pcf?.geographyCountry &&
      Object.keys(CountryLabelToCodeMap).includes(componentDetails?.pcf?.geographyCountry)
    ) {
      return componentDetails?.pcf?.geographyCountry;
    }

    // If region is specified and known, return the region name
    if (
      componentDetails?.pcf?.geographyRegionOrSubregion &&
      Object.keys(RegionLabelToCodeMap).includes(componentDetails?.pcf?.geographyRegionOrSubregion)
    ) {
      return componentDetails?.pcf?.geographyRegionOrSubregion;
    }

    // If neither is specified or known, return 'Not Specified'
    return t('global.notSpecified');
  };

  return (
    <div
      className={classNames(
        'flex flex-column gap-6 mt-2 md:mt-4 w-12 md:w-8',
        styles['component-details-tab'],
      )}
    >
      {/* General Details */}
      <div className='flex flex-column gap-4'>
        <h4 className='-mb-1'>
          {t('componentDetailsPage.tabs.componentDetailsTab.sections.generalDetailsSection.title')}
        </h4>

        <div className='grid grid-nogutter gap-4 md:gap-0'>
          {/* Supplier ID */}
          <div className='col-12 md:col-3 md:pr-4 flex flex-column md:gap-2 gap-1'>
            <span className='font-medium'>
              {t(
                'componentDetailsPage.tabs.componentDetailsTab.sections.generalDetailsSection.supplierId',
              )}
            </span>
            {isLoading ? (
              <Skeleton height='1rem' className='pt-4 w-full' />
            ) : (
              <div className='value-text-faded'>
                {componentDetails?.productIds?.[0]?.split(':').pop() ?? t('global.notSpecified')}
              </div>
            )}
          </div>

          {/* Your ID */}
          <div className='col-12 md:col-3 md:pr-4 flex flex-column md:gap-0 gap-1'>
            <span className='font-medium'>
              {t(
                'componentDetailsPage.tabs.componentDetailsTab.sections.generalDetailsSection.yourId.label',
              )}
            </span>

            {isLoading ? (
              <Skeleton height='1rem' className='pt-4 w-full mt-2' />
            ) : (
              <ComponentInplaceEdit
                fieldId='ownProductId'
                value={componentDetails?.ownProductId ?? ''}
                undefinedValue={t(
                  'componentDetailsPage.tabs.componentDetailsTab.sections.generalDetailsSection.yourId.undefinedValue',
                )}
                disabled={user?.company?.license === 'basic'}
              />
            )}
          </div>

          {/* Supplier */}
          <div className='col-12 md:col-3 md:pr-4 flex flex-column md:gap-2 gap-1'>
            <span className='font-medium'>
              {t(
                'componentDetailsPage.tabs.componentDetailsTab.sections.generalDetailsSection.supplier',
              )}
            </span>

            {isLoading ? (
              <Skeleton height='1rem' className='pt-4 w-full' />
            ) : (
              <div className='value-text-faded'>
                {componentDetails?.companyName ?? t('global.notSpecified')}
              </div>
            )}
          </div>

          {/* Location */}
          <div className='col-12 md:col-3 flex flex-column md:gap-2 gap-1'>
            <span className='font-medium'>
              {t(
                'componentDetailsPage.tabs.componentDetailsTab.sections.generalDetailsSection.location',
              )}
            </span>

            {isLoading ? (
              <Skeleton height='1rem' className='pt-4 w-full' />
            ) : (
              <div className='flex flex-row md:gap-2 gap-1 align-items-center value-text-faded'>
                {getFlagIcon()}
                {getLocationText()}
              </div>
            )}
          </div>
        </div>

        {/* Component Description */}
        <div className='flex flex-column md:gap-2 gap-1'>
          <span className='font-medium'>
            {t(
              'componentDetailsPage.tabs.componentDetailsTab.sections.generalDetailsSection.componentDescription',
            )}
          </span>

          {isLoading ? (
            <Skeleton height='4rem' className='pt-4 w-full' />
          ) : (
            <div className='value-text-faded'>
              {componentDetails?.productDescription ?? t('global.notSpecified')}
            </div>
          )}
        </div>
      </div>

      <Divider className='p-0 m-0 visible md:hidden' />

      {/* Active PCF */}
      <ActivePcfDetails data={mapComponentToActivePcf(componentDetails)} isLoading={isLoading} />
    </div>
  );
};

export default ComponentDetailsTab;
